/* @flow */
// typecheck: ignore file
import qs from 'qs';

import API from 'lib/RemindAPI';
import { dispatcher } from 'lib/Dispatcher';
import { ActionTypes } from 'groups/constants/GroupConstants';
import File from 'entities/File';
import Message from 'entities/Message';
import Messages from 'collections/Messages';
import ToastActions from 'actions/ToastActions';
import { getGroupKey } from 'utils/roleHelpers';
import { genericCatch } from 'lib/promise';

import deliverySummariesQuery from 'chat/queries/deliverySummariesQuery.gql';

import type { Target } from 'entities/Message';

const MESSAGES_PAGE_SIZE: number = 10;

const MessageActions = {

  // XXX (rgoldfinger) remove when cleaning up old way of sending messages
  loadMessages(groupId: number, maxSeq: ?number = null, sinceSeq: number = 0,
    limit: number = MESSAGES_PAGE_SIZE) {
    const options = {
      max_seq: maxSeq,
      since_seq: sinceSeq,
      limit,
    };

    return Messages.yieldMessagesForGroupId(groupId, options)
      .then(messages => {
        dispatcher.dispatch({
          type: ActionTypes.LIST_GROUP_MESSAGES,
          groupKey: groupId,
          messages,
        });
      });
  },

  loadThreadMessages(groupId: number, threadUuid: string, role: string, opts: ?Object = {}): void {
    const options = {
      max_seq: null,
      since_seq: 0,
      limit: MESSAGES_PAGE_SIZE,
      ...opts,
    };

    Messages.yieldThreadMessagesForGroupId(groupId, threadUuid, options)
      .then(messages => {
        dispatcher.dispatch({
          type: ActionTypes.LIST_GROUP_MESSAGES,
          groupKey: getGroupKey(groupId, role),
          messages,
        });
      })
      .catch(genericCatch);
  },

  // XXX (rgoldfinger) remove when cleaning up old way of sending messages
  loadScheduledMessages(groupId: number): void {
    Messages.yieldScheduledMessagesForGroupId(groupId)
      .then(messages => {
        dispatcher.dispatch({
          type: ActionTypes.LIST_GROUP_SCHEDULED_MESSAGES,
          groupKey: groupId,
          messages,
        });
      })
      .catch(genericCatch);
  },

  loadScheduledThreadMessages(groupId: number, threadUuid: string, role: string): void {
    Messages.yieldScheduledThreadMessagesForGroupId(groupId, threadUuid)
      .then(messages => {
        dispatcher.dispatch({
          type: ActionTypes.LIST_GROUP_SCHEDULED_MESSAGES,
          groupKey: getGroupKey(groupId, role),
          messages,
        });
      })
      .catch(genericCatch);
  },

  finishShowingDraft(): void {
    dispatcher.dispatch({
      type: ActionTypes.FINISH_SHOWING_DRAFT,
    });
  },

  saveAndShowDraftAnnouncement(message: Message): void {
    dispatcher.dispatch({
      type: ActionTypes.SAVE_AND_SHOW_DRAFT_ANNOUNCEMENT,
      message,
    });
  },

  saveDraftAnnouncement(message: Message): void {
    dispatcher.dispatch({
      type: ActionTypes.SAVE_DRAFT_ANNOUNCEMENT,
      message,
    });
  },

  saveAndShowDraftFromQuery(actionData: string): void {
    const { body, attachmentId } = qs.parse(actionData);
    const message = new Message({ body, attachmentId });

    dispatcher.dispatch({
      type: ActionTypes.SAVE_AND_SHOW_DRAFT_ANNOUNCEMENT,
      message,
    });
  },

  clearDraftAnnouncement(): void {
    dispatcher.dispatch({
      type: ActionTypes.CLEAR_DRAFT_ANNOUNCEMENT,
    });
  },

  sendMessage(message: Message): Promise<Message> {
    const isScheduled = !!message.getSendAt();
    const type = isScheduled ? {
      success: ActionTypes.SEND_GROUP_SCHEDULED_MESSAGE,
      error: ActionTypes.SEND_GROUP_SCHEDULED_MESSAGE_ERROR,
    } : {
      success: ActionTypes.SEND_GROUP_MESSAGE,
      error: ActionTypes.SEND_GROUP_MESSAGE_ERROR,
    };

    return message.send().then(messageSent => {
      if (isScheduled) {
        ToastActions.showSuccessToast('Announcement scheduled');
      } else {
        const groupsCount = messageSent.getTargets().length;
        ToastActions.showSuccessToast(groupsCount <= 1 ? 'Announcement sent' : `${groupsCount} Announcements sent`);
      }

      dispatcher.dispatch({
        type: type.success,
        message: messageSent,
      });
    }).catch(error => {
      ToastActions.showAPIError(error);
      dispatcher.dispatch({
        type: type.error,
        message,
        error,
      });
    });
  },

  export(groupIds: number[], startDate: Date, endDate: Date): void {
    Messages.export(groupIds, startDate, endDate).then(() => {
      ToastActions.showSuccessToast('We’ve emailed you the Announcement history you’ve requested. You should receive it within 10 minutes.');
    }).catch(error => {
      ToastActions.showAPIError(error);
      throw error;
    });
  },

  loadDeliverySummary(message: Message): void {
    const messageUuids = [message.getUuid()];
    API.execute(deliverySummariesQuery, { messageUuids })
      .then(({ deliverySummaries }) => {
        deliverySummaries.forEach(summary => {
          // elements of the response array may be null
          // https://github.com/remind101/r101-apollo/blob/4ff2bca88dcff6b2cb639d3095fcbe2b75d7756c/src/data/schemas/Root.js#L8
          // TODO consider setting a schema policy that response arrays are compacted
          if (summary === null) {
            return;
          }
          dispatcher.dispatch({
            messageId: message.id,
            type: ActionTypes.LOAD_DELIVERY_SUMMARY,
            summary,
          });
        });
      })
      .catch(genericCatch);
  },

  updateScheduledMessage(
    messageToUpdate: Message,
    body: string,
    targets: Target[],
    files: File[],
    sendAt: Date): Promise<*> {
    const changes = messageToUpdate.update(body, targets, sendAt) || {};

    return messageToUpdate.patch(changes, files).then(message => {
      ToastActions.showSuccessToast('Announcement updated');
      dispatcher.dispatch({
        type: ActionTypes.UPDATED_GROUP_SCHEDULED_MESSAGE,
        message,
      });
    }).catch(error => {
      ToastActions.showAPIError(error);
      throw error;
    });
  },

  destroyScheduledMessage(messageToDestroy: Message): void {
    messageToDestroy.destroy().then(message => {
      ToastActions.showSuccessToast('Announcement deleted');
      dispatcher.dispatch({
        type: ActionTypes.DESTROY_GROUP_SCHEDULED_MESSAGE,
        message,
      });
    }).catch(error => {
      ToastActions.showAPIError(error);
      throw error;
    });
  },
};

export default MessageActions;



// WEBPACK FOOTER //
// ./src/groups/actions/MessageActions.js